import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { IMAGES, BRAND } from "@/lib/data";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.12 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const yAccent = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  const lines = ["Groceries,", "fresh & fair —", "right around", "the corner."];

  return (
    <section
      ref={ref}
      className="relative min-h-screen pt-32 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-end min-h-[80vh]">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="overline text-accent mb-8"
            data-testid="hero-overline"
          >
            {BRAND.locality} · Super Market Since {BRAND.established}
          </motion.p>

          <h1 className="font-display text-foreground leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-[5.7rem]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? (
                    <em className="italic text-accent">{line}</em>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 max-w-xl"
          >
            <p className="text-muted-foreground text-base leading-relaxed">
              {BRAND.tagline} Everyday essentials, fresh produce and doorstep
              delivery in Dabok, Udaipur.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() =>
                document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group inline-flex items-center gap-3 bg-foreground text-primary-foreground px-8 py-4 text-xs font-mono-label uppercase tracking-widest hover:bg-accent transition-colors duration-300"
              data-testid="hero-explore-btn"
            >
              What We Stock
              <ArrowDown size={15} className="group-hover:translate-y-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() =>
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex items-center border border-foreground/25 px-8 py-4 text-xs font-mono-label uppercase tracking-widest hover:border-foreground transition-colors duration-300"
              data-testid="hero-visit-btn"
            >
              Visit / Order
            </button>

            <div className="flex items-center gap-2 pl-2" data-testid="hero-rating">
              <span className="flex text-accent">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                ))}
              </span>
              <span className="font-mono-label text-xs text-muted-foreground">
                {BRAND.rating} / 5 locally rated
              </span>
            </div>
          </motion.div>
        </div>

        {/* Right imagery */}
        <div className="lg:col-span-5 relative h-[62vh] lg:h-[76vh]">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 overflow-hidden"
          >
            <motion.img
              src={IMAGES.heroMain}
              alt="DV Mart supermarket aisle"
              style={{ y }}
              className="w-full h-[120%] object-cover"
              data-testid="hero-image-main"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: yAccent }}
            className="absolute -left-6 sm:-left-12 bottom-8 w-40 sm:w-52 aspect-[4/5] overflow-hidden border-4 border-background shadow-[0_20px_40px_-15px_rgba(0,0,0,0.25)]"
          >
            <img
              src={IMAGES.heroAccent}
              alt="Fresh produce"
              className="w-full h-full object-cover"
              data-testid="hero-image-accent"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
